/* eslint-disable max-lines */
import { useAIRecommendation } from '@hakimo-ui/hakimo/data-access';
import {
  Situation,
  SituationColor,
  SOPException,
  SOPNote,
  SOPWorkflow,
  UnifiedAlarmGroup,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { openFaceComparisonTab } from '@hakimo-ui/hakimo/util';
import clsx from 'clsx';
import { ReactNode, useCallback, useState } from 'react';
import {
  isBlacklisted,
  scanFaceToUnauthorizedFace,
  toUnauthorizedFaces,
} from '../matched-faces/util';
import CameraLabels from '../sop-manager/camera-labels/CameraLabels';
import { ScheduleLabels } from '../sop-manager/schedule-labels/ScheduleLabels';
import TimeRangeLabels from '../sop-manager/time-range-labels/TimeRangeLabels';
import {
  REFETCH_INTERVAL,
  RESOLVE_RECOMMENDATION,
} from './ai-recommendation/utils';
import { getRelevantSchedules } from './scheduleUtils';
import { FaceImageProps, UnauthorizedFace } from './util';

export const UNAUTHORIZED_FACE_GUIDELINE_URL =
  'https://drive.google.com/file/d/1PAH4v5AS1RLOrzCaWtzct6h4oVzwSPsw/view?usp=sharing';

export function getZeroToleranceTextColor(
  isZeroToleranceSite?: boolean
): string {
  return isZeroToleranceSite
    ? 'text-ondark-text-1'
    : 'text-onlight-text-1 dark:text-ondark-text-1';
}

export function createSectionsContent(
  sopWorkflow: SOPWorkflow | undefined
): Record<string, { title: string; content: ReactNode }> {
  return {
    exceptions: {
      title: 'Exceptions (Do not escalate if observed)',
      content: <SOPItemList items={sopWorkflow?.exceptions ?? []} />,
    },
    notes: {
      title: 'Notes',
      content: <SOPItemList items={sopWorkflow?.notes ?? []} />,
    },
  };
}

export function extractUnauthorizedFaces(
  alarmGroup: UnifiedAlarmGroup
): UnauthorizedFace[] {
  const unauthorizedFaces: UnauthorizedFace[] = [];

  if (!alarmGroup?.alarms) {
    return unauthorizedFaces;
  }

  alarmGroup.alarms.forEach((alarm) => {
    // Remote guarding carries the type on the group only; Scan sets it per
    // alarm. Prefer the alarm's own type where there is one.
    const alarmType = alarm.alarm_type ?? alarmGroup.locationAlarmType;
    if (alarm.media?.type === 'frames' && alarm.media.frames) {
      alarm.media.frames.forEach((frame) => {
        frame.matched_faces?.forEach((face) => {
          if (isBlacklisted(face.profile_type)) {
            unauthorizedFaces.push(
              scanFaceToUnauthorizedFace(face, frame.timestamp, {
                cameraName: alarm.cameraName,
                frameUrl: frame.url,
                alarmType,
              })
            );
          }
        });
      });
    } else if (
      alarm.media?.type === 'video' &&
      alarm.media.video?.matchedFaces
    ) {
      unauthorizedFaces.push(
        ...toUnauthorizedFaces(alarm.media.video.matchedFaces, {
          cameraName: alarm.cameraName,
          videoStartTime: alarm.media.video.startTime,
          alarmType,
        })
      );
    }
  });

  return unauthorizedFaces;
}

export function useShouldHideAIRecommendation(
  locationAlarmId: string,
  product: UnifiedMonitoringProduct,
  unauthorizedFaces?: UnauthorizedFace[]
): boolean {
  const { data: aiRecommendationData } = useAIRecommendation(
    locationAlarmId,
    product,
    REFETCH_INTERVAL
  );
  const aiOperatorRep = aiRecommendationData
    ? aiRecommendationData[0]
    : undefined;
  const isAIRecommendationResolve =
    aiOperatorRep?.recommendations === RESOLVE_RECOMMENDATION;
  const hasUnauthorizedFaces =
    unauthorizedFaces && unauthorizedFaces.length > 0;

  return !!(isAIRecommendationResolve && hasUnauthorizedFaces);
}

export function FaceImage(props: FaceImageProps) {
  const {
    imagePath,
    profileName,
    employeeId,
    gender,
    timestamp,
    cameraName,
    alarmType,
    frameUrl,
    faceBox,
    fullBodyImagePath,
    personBox,
  } = props;
  const [hasError, setHasError] = useState(false);

  const handleImageError = useCallback(() => {
    setHasError(true);
  }, []);

  const handleClick = useCallback(() => {
    openFaceComparisonTab({
      profileName,
      employeeId,
      gender,
      timestamp,
      cameraName,
      alarmType,
      referencePrimaryUrl: imagePath,
      referenceFullBodyUrl: fullBodyImagePath,
      frameUrl,
      faceBox,
      personBox,
    });
  }, [
    imagePath,
    profileName,
    employeeId,
    gender,
    timestamp,
    cameraName,
    alarmType,
    frameUrl,
    faceBox,
    fullBodyImagePath,
    personBox,
  ]);

  if (hasError) {
    return null;
  }

  return (
    <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-full">
      <button
        type="button"
        onClick={handleClick}
        className="block h-full w-full cursor-pointer"
        title={profileName}
      >
        <img
          src={imagePath}
          alt={profileName}
          className="h-full w-full object-cover"
          onError={handleImageError}
        />
      </button>
    </div>
  );
}

export const CHIP_CLASS =
  'border-onlight-line-2 dark:border-ondark-line-2 flex flex-wrap items-center gap-2 rounded-md border p-2';

export const CHIP_FILL_CLASS = 'bg-onlight-bg-1 dark:bg-transparent';

export const CHIP_IDLE_CLASS =
  'text-onlight-text-2 dark:text-ondark-text-2 hover:bg-onlight-bg-2 hover:dark:bg-ondark-bg-2';

export const CHIP_SELECTED_CLASS =
  'border-primary-500 bg-primary-50 text-primary-700 dark:bg-primary-900/20 dark:text-primary-400';

interface SituationChipsProps {
  situations: Situation[];
  onSelect?: (situation: Situation) => void;
  alarmStartEpochMs?: number;
  alarmEndEpochMs?: number;
  locationTimezone?: string;
  variant?: 'default' | 'filled';
}

export function SituationChips(props: SituationChipsProps) {
  const {
    situations,
    onSelect,
    alarmStartEpochMs,
    alarmEndEpochMs,
    locationTimezone,
    variant = 'default',
  } = props;

  const selectable = onSelect !== undefined;
  const isFilled = variant === 'filled';

  return (
    <div className={clsx('flex flex-wrap gap-2', !isFilled && 'py-1 pr-2')}>
      {situations.map((situation, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onSelect?.(situation)}
          disabled={!selectable}
          className={clsx(
            CHIP_CLASS,
            isFilled && CHIP_FILL_CLASS,
            situation.color === SituationColor.RED &&
              'text-situation-rose-600 dark:text-situation-rose-400',
            situation.color === SituationColor.GREEN &&
              'text-situation-teal-600 dark:text-situation-teal-400',
            situation.color === SituationColor.BLUE &&
              'text-primary-500 dark:text-primary-400',
            selectable && [
              'hover:dark:bg-ondark-bg-2 hover:bg-onlight-bg-2',
              situation.color === SituationColor.RED &&
                'hover:text-situation-rose-700',
              situation.color === SituationColor.GREEN &&
                'hover:text-situation-teal-700',
              situation.color === SituationColor.BLUE &&
                'hover:text-primary-700 dark:hover:text-primary-300',
            ]
          )}
        >
          <span className="text-left text-sm font-medium">
            {situation.label}
          </span>
          <ScheduleLabels
            schedules={getRelevantSchedules(
              situation,
              alarmStartEpochMs,
              alarmEndEpochMs,
              locationTimezone ?? 'UTC'
            )}
          />
          {situation.cameras && situation.cameras.length > 0 && (
            <CameraLabels cameras={situation.cameras} />
          )}
        </button>
      ))}
    </div>
  );
}

interface SOPItemListProps {
  items: (SOPNote | SOPException)[];
}

export function SOPItemList({ items }: SOPItemListProps) {
  if (items.length === 0) {
    return null;
  }

  return (
    <ul className="min-w-0 list-disc space-y-2 overflow-hidden">
      {items.map((item, i) => (
        <li key={i} className="ml-4 list-item">
          <div className="flex flex-wrap items-center gap-2 [overflow-wrap:anywhere]">
            <span>{item.text}</span>
            <TimeRangeLabels timeRange={item.timeRange} />
            <CameraLabels cameras={item.cameras} />
          </div>
        </li>
      ))}
    </ul>
  );
}
